import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, TemplateRef } from '@angular/core';
import { BehaviorSubject, Observable, map, of, retry } from 'rxjs';
import { createQueryParams } from '../../utils/query-params.service';
import { isUndefined } from 'lodash';
import { environment } from '../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private platformId = inject(PLATFORM_ID);

  constructor(private http: HttpClient) { }

  private callbackSuccess = new BehaviorSubject<boolean>(false);
  callbackSuccess$ = this.callbackSuccess.asObservable();

  private callbackAgentSuccess = new BehaviorSubject<boolean>(false);
  callbackAgentSuccess$ = this.callbackAgentSuccess.asObservable();

  setCallbackSuccess(status: boolean) {
    this.callbackSuccess.next(status);
  }

  setAgentCallbackSuccess(status: boolean) {
    this.callbackAgentSuccess.next(status);
  }

  private contactGuideTemplate!: TemplateRef<any>;

  setTemplate(template: TemplateRef<any>): void {
    this.contactGuideTemplate = template;
  }

  getTemplate(): TemplateRef<any> {
    return this.contactGuideTemplate;
  }

  getAll(data?: any): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.apiUrl}/api/customer/dashboard/quotes-by-agent/${data._id}`
    );
  }

  getTripStatus(data?: any): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.apiUrl}/api/customer/dashboard/trip-status/${data}`
    );
  }

  downloadQuotes(quoteId: any): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}/api/customer/dashboard/quotes-download/${quoteId}`,
      {
        responseType: 'arraybuffer',
      }
    );
  }

  getTripsForUser(queryParams?: any) {
    if (!isPlatformBrowser(this.platformId)) {
      return of([]); // Prevent SSR error
    }

    let params = createQueryParams(queryParams);
    return this.http.get<any[]>(
      `${environment.apiUrl}/api/customer/dashboard/customer-leads?${params}`
    );
  }
  getTripsDestinations() {
    return this.http.get<any[]>(
      `${environment.apiUrl}/api/customer/dashboard/customer-leads-destination`
    );
  }
  save(data: any): Observable<any[]> {
    if (data._id) {
      return this.http.put<any[]>(
        `${environment.apiUrl}/api/quotes/${data._id}`,
        data
      );
    } else {
      return this.http.post<any[]>(`${environment.apiUrl}/api/quotes`, data);
    }
  }
  delete(id: any): Observable<any[]> {
    return this.http.delete<any[]>(`${environment.apiUrl}/api/quotes/${id}`);
  }

  getById(id: any): Observable<any> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/quotes/${id}`);
  }

  emailSend(data: any): Observable<any> {
    return this.http.post<any[]>(
      `${environment.apiUrl}/api/quotes/send-mail`,
      data
    );
  }

  quotePreview(id: any) {
    return this.http.get(`${environment.apiUrl}/api/quotes/pdf/${id}`, {
      responseType: 'blob',
    });
  }

  cloneQuote(id: any) {
    return this.http.post(`${environment.apiUrl}/api/quotes/clone`, {
      quoteId: id,
    });
  }

  getAllQuotationsByLeadId(id: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/quotes/lead/${id}`);
  }

  rejectQuote(data: any): Observable<any> {
    return this.http.put(
      `${environment.apiUrl}/api/customer/dashboard/reject-quote`,
      data
    );
  }
  rejectTrip(leadId: any, data: any): Observable<any> {
    const formDataWithLeadId = {
      ...data,
      leadId: leadId,
    };
    return this.http.put(
      `${environment.apiUrl}/api/customer/dashboard/reject-trip`,
      formDataWithLeadId
    );
  }
  getByID(id: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.apiUrl}/api/customer/profile/${id}`
    );
  }

  update(data: any): Observable<any[]> {
    const id = this.getUserId();
    return this.http.put<any[]>(
      `${environment.apiUrl}/api/customer/profile/${id}`,
      data
    );
  }

  // getAllStates(data?: any): Observable<any> {
  //   return this.http.get<any>(`${environment.apiUrl}/api/dms/country/states/`, data);
  // }

  getUserId(): string | null {
    const userId = localStorage.getItem('userId');
    return userId;
  }

  postUserCallBackRequestById(data: any): Observable<any[]> {
    const id = this.getUserId();
    return this.http.post<any[]>(`${environment.apiUrl}/api/customer/callback-requests`, data);
  }

  updateProfileImage(
    file: File,
    uniqueId: string,
    data?: any
  ): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (!isUndefined(data)) {
      Object.keys(data).forEach((key) => {
        const value = data[key];
        if (typeof value === 'object' && value !== null) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value.toString());
        }
      });
    }
    formData.append('uniqueId', uniqueId);

    // Create a new HttpRequest
    const req = new HttpRequest(
      'POST',
      `${environment.apiUrl}/api/gfs`,
      formData,
      {
        reportProgress: true,
      }
    );

    return this.http.request(req).pipe(
      map((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            // Calculate the progress percentage
            const progress = event.total
              ? Math.round((100 * event.loaded) / event.total)
              : 0;
            return progress;

          case HttpEventType.Response:
            return { response: event.body, uniqueId };

          default:
            return 0;
        }
      }),
      retry(3)
    );
  }
  acceptQuote(quoteId: any): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}/api/customer/dashboard/accept-quote/${quoteId}`
    );
  }
  getInvoice(id: any) {
    return this.http.get(
      `${environment.apiUrl}/api/customer/dashboard/invoice/${id}`
    );
  }

  downloadInvoice(id: string): Observable<ArrayBuffer> {
    return this.http.get(
      `${environment.apiUrl}/api/agent/invoice/download-invoice-pdf/${id}`,
      {
        responseType: 'arraybuffer',
      }
    );
  }

  updateQuote(quoteid: any, data: any) {
    return this.http.post(
      `${environment.apiUrl}/api/customer/dashboard/quotes/change-requests/${quoteid}`,
      data
    );
  }
  reactivateQuote(quoteid: any) {
    return this.http.get(
      `${environment.apiUrl}/api/customer/dashboard/reactivate-quote/${quoteid}`
    );
  }
  getTripInfo(leadId: string): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}/api/customer/dashboard/leadInfo/${leadId}`
    );
  }

  requestCallback(data: any): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/api/customer/callback-requests`,
      data
    );
  }

  compareQuotes(data: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/customer/dashboard/compare-quotes/${data.leadId}`, {
      quoteIds: data.quoteIds,
    });
  }
  leadCompareQuotes(leadId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/customer/dashboard/compare/${leadId}`);
  }

  getFaqData() {
    return this.http.get<any>(`${environment.apiUrl}/api/public/traveller-faqs`);
  }

  saveContactForm(data: any): Observable<any> {
    return this.http.post<any>(`api/cms/contacts`, data);
  }

  getDestinationFaqs(id: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/public/destination-faqs/${id}`);
  }

  getLeadsCount(data?: any) {
    if (data) {
      return this.http.get<any>(`${environment.apiUrl}/api/customer/dashboard/customer-leads-stats?destination=${data.destination}`);
    } else {
      return this.http.get<any>(`${environment.apiUrl}/api/customer/dashboard/customer-leads-stats`);
    }
  }

  verifyToken(token: any) {
    return this.http.post<any>(
      `${environment.apiUrl}/api/customer/auth/verify/token`,
      token
    );
  }
}
